/**
 * PATH: packages/drafting/src/panels/ExportPanel.tsx
 * FILE_ID: DRAFTING-PANEL-EXPORT-001
 * PURPOSE: Export buttons (PNG/SVG/JSON) + the live validation report so
 *   the user sees geometry/scale warnings before they export or (later)
 *   email the drawing.
 */
import { FileImage, FileCode, FileJson, CheckCircle2, AlertTriangle, XCircle, Mail, Paperclip } from 'lucide-react'
import type { ValidationReport } from '../ir/schema'

export interface ExportPanelProps {
  report: ValidationReport
  onPng: () => void
  onSvg: () => void
  onJson: () => void
  /** When set, show "Insert into email" (drops a PNG into the compose body). */
  onInsertImage?: () => void
  /** When set, show "Attach to email" (adds a PNG to the compose attachments). */
  onAttach?: () => void
}

const btn =
  'flex flex-1 items-center justify-center gap-1.5 rounded-md border border-slate-300 px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800'

export function ExportPanel(props: ExportPanelProps) {
  const { report, onPng, onSvg, onJson, onInsertImage, onAttach } = props
  const errors = report.errors.filter((e) => e.severity === 'error')
  const warns = report.errors.filter((e) => e.severity === 'warn')

  return (
    <section className="space-y-2">
      <h3 className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Export</h3>
      <div className="flex gap-1.5">
        <button type="button" className={btn} onClick={onPng}>
          <FileImage className="h-3.5 w-3.5" aria-hidden /> PNG
        </button>
        <button type="button" className={btn} onClick={onSvg}>
          <FileCode className="h-3.5 w-3.5" aria-hidden /> SVG
        </button>
        <button type="button" className={btn} onClick={onJson}>
          <FileJson className="h-3.5 w-3.5" aria-hidden /> JSON
        </button>
      </div>

      {(onInsertImage || onAttach) && (
        <div className="flex gap-1.5">
          {onInsertImage && (
            <button type="button" className={btn} onClick={onInsertImage}>
              <Mail className="h-3.5 w-3.5" aria-hidden /> Insert
            </button>
          )}
          {onAttach && (
            <button type="button" className={btn} onClick={onAttach}>
              <Paperclip className="h-3.5 w-3.5" aria-hidden /> Attach
            </button>
          )}
        </div>
      )}

      <div className="rounded-md border border-slate-200 px-2 py-1.5 text-xs dark:border-slate-700">
        {errors.length === 0 && warns.length === 0 ? (
          <p className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Drawing valid
          </p>
        ) : (
          <ul className="space-y-1">
            {errors.map((e, i) => (
              <li key={`er${i}`} className="flex items-start gap-1.5 text-rose-600 dark:text-rose-400">
                <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>{e.msg}</span>
              </li>
            ))}
            {warns.map((w, i) => (
              <li key={`wr${i}`} className="flex items-start gap-1.5 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>{w.msg}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
